import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { filter, mergeMap, pipe, Subject, switchMap, takeUntil, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  normalizePickupRequestStatus,
  PickupRequest,
  PickupRequestStatus,
} from './entities/pickup-request.model';
import { LivePickupService } from './infra/live-pickup.service';
import { LivePickupRealtimeService } from './infra/live-pickup-realtime.service';
import type {
  AccessPointOption,
  CompletedStudent,
  HeroDisplayType,
  HeroStudent,
  SystemStatus,
  WaitingStudent,
} from './live-pickup.types';
import { DateTime } from 'luxon';
import { getDateTime, playAlertSound } from '@shared/utils';
import { LinkShortenerService } from '@shared/services';
import { APP_CONFIG } from '@application/config';

/**
 * Tags the panel's short links as Ediz. The host that goes with it is
 * `externalUrl.link_shortener_ediz`, read from `APP_CONFIG`.
 */
const PANEL_LINK_SOURCE_API = 'ediz';

const HIGHLIGHT_DURATION_MS = 3000;
const COMPLETED_MAX_KEEP = 50;

function formatWaitTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function toWaitingStudent(p: PickupRequest, now: DateTime): WaitingStudent {
  // `getDateTime` devolve null/inválido quando o backend não mandou a data — nesse caso a espera é 0.
  const requestedAt = getDateTime(p.RequestedOnUtc);
  const waitSeconds = requestedAt?.isValid
    ? Math.max(0, Math.floor(now.diff(requestedAt, 'seconds').seconds))
    : 0;
  return {
    id: p.PickupRequestId,
    studentPersonId: p.StudentPersonId,
    name: p.StudentDisplayName,
    // TODO(live-pickup): reativar quando o backend enviar StudentClassDisplay.
    // className: p.StudentClassDisplay,
    responsibleName: p.GuardianDisplayName ?? '',
    gate: p.GateLabel,
    waitTime: formatWaitTime(waitSeconds),
    waitSeconds,
  };
}

function toCompletedStudent(p: PickupRequest): CompletedStudent {
  return {
    id: p.PickupRequestId,
    studentPersonId: p.StudentPersonId,
    name: p.StudentDisplayName,
    // TODO(live-pickup): reativar quando o backend enviar StudentClassDisplay.
    // className: p.StudentClassDisplay,
    gate: p.GateLabel,
    completedOnUtc: p.CompletedOnUtc ?? DateTime.now().toISO(),
  };
}

export function toHeroStudent(
  p: PickupRequest,
  displayType: HeroDisplayType,
): HeroStudent {
  return {
    id: p.PickupRequestId,
    studentPersonId: p.StudentPersonId,
    name: p.StudentDisplayName,
    // TODO(live-pickup): reativar quando o backend enviar StudentClassDisplay.
    // className: p.StudentClassDisplay,
    gate: p.GateLabel,
    responsibleName: p.GuardianDisplayName ?? '',
    responsiblePersonId: p.GuardianPersonId ?? null,
    recognizedOnUtc: p.RecognizedOnUtc ?? DateTime.now().toISO(),
    photoUrl: '',
    displayType,
  };
}

function sortByRequestedAsc(items: PickupRequest[]): PickupRequest[] {
  return [...items].sort((a, b) =>
    (a.RequestedOnUtc ?? '').localeCompare(b.RequestedOnUtc ?? ''),
  );
}

function sortBatchByStudentName(items: PickupRequest[]): PickupRequest[] {
  return [...items].sort((a, b) =>
    (a.StudentDisplayName ?? '').localeCompare(
      b.StudentDisplayName ?? '',
      undefined,
      { sensitivity: 'base' },
    ),
  );
}

function pickMostRecentForHero(items: PickupRequest[]): PickupRequest | null {
  if (!items.length) return null;
  return [...items].sort((a, b) => {
    const byRequested = (b.RequestedOnUtc ?? '').localeCompare(
      a.RequestedOnUtc ?? '',
    );
    if (byRequested !== 0) return byRequested;
    return (a.StudentDisplayName ?? '').localeCompare(
      b.StudentDisplayName ?? '',
      undefined,
      { sensitivity: 'base' },
    );
  })[0];
}

function sortByCompletedDesc(items: PickupRequest[]): PickupRequest[] {
  return [...items].sort((a, b) =>
    (b.CompletedOnUtc ?? '').localeCompare(a.CompletedOnUtc ?? ''),
  );
}

export type LivePickupHubEvent =
  | { type: 'created'; items: PickupRequest[] }
  | { type: 'updated'; item: PickupRequest };

export interface LivePickupState {
  tenantId: string | null;
  accessPointId: string | null;
  accessPointName: string | null;
  accessPoints: AccessPointOption[];
  pickupRequests: PickupRequest[];
  completedRequests: PickupRequest[];
  latestHero: PickupRequest | null;
  openPickupsCount: number | null;
  lastUpdate: DateTime;
  systemStatus: SystemStatus;
  highlightedIds: string[];
  currentTime: DateTime;
  confirmingPickupIds: string[];
  lastHubEvent: LivePickupHubEvent | null;
  /**
   * Short link keyed by the full URL: the panel token is the whole session, so a
   * rotated token yields a new URL and must not reuse the old short link.
   */
  shortenedLinksCache: Record<string, string>;
}

/**
 * Estado inicial como FUNÇÃO, não como `const`.
 *
 * `lastUpdate` e `currentTime` são relógios: num `const` de módulo, o
 * `DateTime.now()` corria ao carregar o bundle — antes de o
 * `LUXON_ZONE_INITIALIZER` pôr o fuso da conta em `Settings.defaultZone` — e o
 * valor ficava congelado assim para a sessão inteira, no fuso da máquina.
 * `withState` aceita uma factory e chama-a quando o store é instanciado, já com
 * o fuso em vigor.
 */
const initialState = (): LivePickupState => ({
  tenantId: null,
  accessPointId: null,
  accessPointName: null,
  accessPoints: [],
  pickupRequests: [],
  completedRequests: [],
  latestHero: null,
  openPickupsCount: null,
  lastUpdate: DateTime.now(),
  systemStatus: 'offline',
  highlightedIds: [],
  currentTime: DateTime.now(),
  confirmingPickupIds: [],
  lastHubEvent: null,
  shortenedLinksCache: {},
});

export const LivePickupStore = signalStore(
  { providedIn: 'root' },
  withState<LivePickupState>(initialState),
  withReqState(
    'loadOpenPickups',
    'loadRecentCompleted',
    'loadLatestHero',
    'loadOpenPickupsCount',
    'confirmPickup',
    'shortenLink',
  ),
  withComputed((store) => ({
    waitingCount: computed(
      () => store.openPickupsCount() ?? store.pickupRequests().length,
    ),
    waitingStudents: computed<WaitingStudent[]>(() => {
      const now = store.currentTime();
      return store.pickupRequests().map((p) => toWaitingStudent(p, now));
    }),
    completedStudents: computed<CompletedStudent[]>(() =>
      store.completedRequests().map(toCompletedStudent),
    ),
    heroStudent: computed<HeroStudent | null>(() => {
      const top = store.latestHero();
      if (!top) return null;
      const displayType: HeroDisplayType = store
        .highlightedIds()
        .includes(top.PickupRequestId)
        ? 'arrival'
        : 'recognized';
      return toHeroStudent(top, displayType);
    }),
    heroCompletedStudent: computed<HeroStudent | null>(() => {
      const top = store.completedRequests()[0];
      if (!top) return null;
      return toHeroStudent(top, 'recognized');
    }),
  })),
  withMethods((
    store,
    livePickupService = inject(LivePickupService),
    realtimeService = inject(LivePickupRealtimeService),
    errorStore = inject(ErrorStore),
    linkShortenerService = inject(LinkShortenerService),
    appConfig = inject(APP_CONFIG),
  ) => {
    let destroy$ = new Subject<void>();
    let tickIntervalId: ReturnType<typeof setInterval> | null = null;

    const startTick = (): void => {
      if (tickIntervalId) return;
      tickIntervalId = setInterval(() => {
        patchState(store, { currentTime: DateTime.now() });
      }, 1000);
    };

    const stopTick = (): void => {
      if (tickIntervalId) {
        clearInterval(tickIntervalId);
        tickIntervalId = null;
      }
    };

    const clearHighlightAfter = (id: string): void => {
      setTimeout(() => {
        patchState(store, {
          highlightedIds: store
            .highlightedIds()
            .filter((hid) => hid !== id),
        });
      }, HIGHLIGHT_DURATION_MS);
    };

    const handlePickupCreated = (items: PickupRequest[]): void => {
      if (!items.length) return;
      const batch = sortBatchByStudentName(items);
      const map = new Map(
        store.pickupRequests().map((p) => [p.PickupRequestId, p]),
      );
      batch.forEach((it) => map.set(it.PickupRequestId, it));
      const merged = sortByRequestedAsc(Array.from(map.values()));
      const newIds = batch.map((i) => i.PickupRequestId);
      const candidate = pickMostRecentForHero(batch);
      const current = store.latestHero();
      const nextHero =
        candidate &&
        (!current ||
          pickMostRecentForHero([candidate, current])?.PickupRequestId ===
            candidate.PickupRequestId)
          ? candidate
          : current;
      const currentCount = store.openPickupsCount();
      patchState(store, {
        pickupRequests: merged,
        latestHero: nextHero,
        openPickupsCount:
          currentCount === null ? null : currentCount + batch.length,
        highlightedIds: [...store.highlightedIds(), ...newIds],
        lastUpdate: DateTime.now(),
        lastHubEvent: { type: 'created', items },
      });
      playAlertSound('live-pickup');
      newIds.forEach(clearHighlightAfter);
    };

    const handlePickupUpdated = (item: PickupRequest): void => {
      const status = normalizePickupRequestStatus(item.Status);
      const isClosed =
        status === PickupRequestStatus.Completed ||
        status === PickupRequestStatus.Cancelled;
      const currentHero = store.latestHero();
      if (!isClosed) {
        const exists = store
          .pickupRequests()
          .some((p) => p.PickupRequestId === item.PickupRequestId);
        const next = exists
          ? store
              .pickupRequests()
              .map((p) => (p.PickupRequestId === item.PickupRequestId ? item : p))
          : [...store.pickupRequests(), item];
        const heroAfterUpdate =
          currentHero?.PickupRequestId === item.PickupRequestId
            ? item
            : currentHero;
        patchState(store, {
          pickupRequests: sortByRequestedAsc(next),
          latestHero: heroAfterUpdate,
          lastUpdate: DateTime.now(),
          lastHubEvent: { type: 'updated', item },
        });
        return;
      }
      const remaining = store
        .pickupRequests()
        .filter((p) => p.PickupRequestId !== item.PickupRequestId);
      const completed =
        status === PickupRequestStatus.Completed
          ? sortByCompletedDesc([
              item,
              ...store
                .completedRequests()
                .filter((p) => p.PickupRequestId !== item.PickupRequestId),
            ]).slice(0, COMPLETED_MAX_KEEP)
          : store.completedRequests();
      const heroAfterClose =
        currentHero?.PickupRequestId === item.PickupRequestId
          ? pickMostRecentForHero(remaining)
          : currentHero;
      const currentCount = store.openPickupsCount();
      patchState(store, {
        pickupRequests: remaining,
        completedRequests: completed,
        latestHero: heroAfterClose,
        openPickupsCount:
          currentCount === null ? null : Math.max(0, currentCount - 1),
        lastUpdate: DateTime.now(),
        lastHubEvent: { type: 'updated', item },
      });
    };

    const loadOpenPickups = rxMethod<string | null>(
      pipe(
        tap(() => patchState(store, setPropLoading('loadOpenPickups'))),
        switchMap((accessPointId) =>
          (accessPointId
            ? livePickupService.getOpenPickups(accessPointId)
            : livePickupService.getAllOpenPickups()
          ).pipe(
            tapResponse({
              next: (res) =>
                patchState(store, {
                  pickupRequests: sortByRequestedAsc(res.value ?? []),
                  lastUpdate: DateTime.now(),
                  ...setPropLoaded('loadOpenPickups'),
                }),
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(
                  store,
                  setPropError('loadOpenPickups', error),
                );
              },
            }),
          ),
        ),
      ),
    );

    const loadRecentCompleted = rxMethod<string | null>(
      pipe(
        tap(() => patchState(store, setPropLoading('loadRecentCompleted'))),
        switchMap((accessPointId) =>
          (accessPointId
            ? livePickupService.getRecentCompleted(accessPointId)
            : livePickupService.getAllRecentCompleted()
          ).pipe(
            tapResponse({
              next: (res) =>
                patchState(store, {
                  completedRequests: sortByCompletedDesc(res.value ?? []),
                  ...setPropLoaded('loadRecentCompleted'),
                }),
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(
                  store,
                  setPropError('loadRecentCompleted', error),
                );
              },
            }),
          ),
        ),
      ),
    );

    const loadLatestHero = rxMethod<void>(
      pipe(
        tap(() => patchState(store, setPropLoading('loadLatestHero'))),
        switchMap(() =>
          livePickupService.getLatestPickupForHero(store.accessPointId()).pipe(
            tapResponse({
              next: (res) =>
                patchState(store, {
                  latestHero: res.value?.[0] ?? null,
                  ...setPropLoaded('loadLatestHero'),
                }),
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('loadLatestHero', error));
              },
            }),
          ),
        ),
      ),
    );

    const loadOpenPickupsCount = rxMethod<void>(
      pipe(
        tap(() => patchState(store, setPropLoading('loadOpenPickupsCount'))),
        switchMap(() =>
          livePickupService.getOpenPickupsCount().pipe(
            tapResponse({
              next: (res) =>
                patchState(store, {
                  openPickupsCount: res['@odata.count'] ?? 0,
                  ...setPropLoaded('loadOpenPickupsCount'),
                }),
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('loadOpenPickupsCount', error));
              },
            }),
          ),
        ),
      ),
    );

    const confirmPickup = rxMethod<string>(
      pipe(
        tap((pickupId) =>
          patchState(store, {
            confirmingPickupIds: [
              ...store.confirmingPickupIds(),
              pickupId,
            ],
            ...setPropLoading('confirmPickup'),
          }),
        ),
        mergeMap((pickupId) =>
          livePickupService.completePickup(pickupId).pipe(
            tapResponse({
              next: () => {
                patchState(store, {
                  confirmingPickupIds: store
                    .confirmingPickupIds()
                    .filter((id) => id !== pickupId),
                  ...setPropLoaded('confirmPickup'),
                });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, {
                  confirmingPickupIds: store
                    .confirmingPickupIds()
                    .filter((id) => id !== pickupId),
                  ...setPropError('confirmPickup', error),
                });
              },
            }),
          ),
        ),
      ),
    );

    const loadAccessPoints = rxMethod<void>(
      pipe(
        switchMap(() =>
          livePickupService.getAccessPoints().pipe(
            tapResponse({
              next: (res) =>
                patchState(store, {
                  accessPoints: (res ?? []).map((ap) => ({
                    id: ap.AccessPointId,
                    name: ap.Name,
                  })),
                }),
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
              },
            }),
          ),
        ),
      ),
    );

    /**
     * Shortens a URL into `shortenedLinksCache`, keyed by the URL itself. A
     * failure falls back to the original URL instead of alerting: the long link
     * still works, and the user cannot act on a shortener outage.
     */
    const shortenLink = rxMethod<string>(
      pipe(
        filter((url) => !!url && !store.shortenedLinksCache()[url]),
        tap(() => patchState(store, setPropLoading('shortenLink'))),
        mergeMap((url) =>
          linkShortenerService
            .shortenLink(
              url,
              PANEL_LINK_SOURCE_API,
              appConfig.externalUrl.link_shortener_ediz,
            )
            .pipe(
              tapResponse({
                next: (res) =>
                  patchState(store, {
                    shortenedLinksCache: {
                      ...store.shortenedLinksCache(),
                      [url]: res?.shortUrl ?? url,
                    },
                    ...setPropLoaded('shortenLink'),
                  }),
                error: (error: HttpErrorResponse) =>
                  patchState(store, {
                    shortenedLinksCache: {
                      ...store.shortenedLinksCache(),
                      [url]: url,
                    },
                    ...setPropError('shortenLink', error),
                  }),
              }),
            ),
        ),
      ),
    );

    return {
      loadOpenPickups,
      loadRecentCompleted,
      loadLatestHero,
      loadOpenPickupsCount,
      loadAccessPoints,
      confirmPickup,
      shortenLink,

      getShortenedLink(url: string | null): string | null {
        if (!url) return null;
        return store.shortenedLinksCache()[url] ?? null;
      },

      async switchAccessPoint(
        accessPointId?: string,
        accessPointName?: string,
      ): Promise<void> {
        const tenantId = store.tenantId();
        if (!tenantId) return;
        const prevId = store.accessPointId();
        try {
          if (prevId) {
            await realtimeService.leaveAccessPoint(tenantId, prevId);
          } else {
            await realtimeService.leaveTenant(tenantId);
          }
        } catch {
          // ignore — re-join below
        }
        patchState(store, {
          accessPointId: accessPointId ?? null,
          accessPointName: accessPointName ?? null,
        });
        try {
          await realtimeService.start();
          if (accessPointId) {
            await realtimeService.joinAccessPoint(tenantId, accessPointId);
          } else {
            await realtimeService.joinTenant(tenantId);
          }
        } catch {
          // hubStatus subject already reflects failure
        }
        loadOpenPickups(accessPointId ?? null);
        loadRecentCompleted(accessPointId ?? null);
        loadLatestHero();
      },

      async connect(opts: {
        tenantId: string;
        accessPointId?: string;
        accessPointName?: string;
      }): Promise<void> {
        destroy$?.next();
        destroy$?.complete();
        destroy$ = new Subject<void>();
        patchState(store, {
          tenantId: opts.tenantId,
          accessPointId: opts.accessPointId ?? null,
          accessPointName: opts.accessPointName ?? null,
        });

        startTick();

        realtimeService.pickupCreated$
          .pipe(takeUntil(destroy$))
          .subscribe(handlePickupCreated);
        realtimeService.pickupUpdated$
          .pipe(takeUntil(destroy$))
          .subscribe(handlePickupUpdated);
        realtimeService.hubStatus$
          .pipe(takeUntil(destroy$))
          .subscribe((status) => {
            const systemStatus: SystemStatus =
              status === 'connected'
                ? 'online'
                : status === 'disconnected'
                  ? 'offline'
                  : 'degraded';
            patchState(store, { systemStatus });
          });

        try {
          await realtimeService.start();
          if (opts.accessPointId) {
            await realtimeService.joinAccessPoint(
              opts.tenantId,
              opts.accessPointId,
            );
          } else {
            await realtimeService.joinTenant(opts.tenantId);
          }
        } catch {
          // hubStatus subject already reflects failure
        }
      },

      async disconnect(): Promise<void> {
        const tenantId = store.tenantId();
        const accessPointId = store.accessPointId();
        stopTick();
        destroy$.next();
        destroy$.complete();
        try {
          if (tenantId) {
            if (accessPointId) {
              await realtimeService.leaveAccessPoint(tenantId, accessPointId);
            } else {
              await realtimeService.leaveTenant(tenantId);
            }
          }
        } catch {
          // ignore
        }
        await realtimeService.stop();
        patchState(
          store,
          {
            // A factory já devolve relógios frescos; os dois campos abaixo
            // ficam explícitos porque é isso que o reset promete.
            ...initialState(),
            currentTime: DateTime.now(),
            lastUpdate: DateTime.now(),
          },
          setPropInit(
            'loadOpenPickups',
            'loadRecentCompleted',
            'loadLatestHero',
            'loadOpenPickupsCount',
            'confirmPickup',
            'shortenLink',
          ),
        );
      },

      setSystemStatus(status: SystemStatus): void {
        patchState(store, { systemStatus: status });
      },
    };
  }),
);
